import React from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Facebook, 
  Twitter, 
  Instagram,
  Globe
} from 'lucide-react';
import { useLanguageStore } from '../store/useLanguageStore';
import logo from '../assets/images/logo.jpg';

const Footer: React.FC = () => {
  const { t } = useLanguageStore();

  const quickLinks = [
    { label: 'Emergency Services', labelSinhala: 'හදිසි සේවාවන්', href: '/emergency' },
    { label: 'Departments', labelSinhala: 'දෙපාර්තමේන්තු', href: '/departments' },
    { label: 'Doctors', labelSinhala: 'වෛද්‍යවරුන්', href: '/doctors' },
    { label: 'Visiting Hours', labelSinhala: 'නැරඹුම් වේලාවන්', href: '/visiting-hours' },
    { label: 'Pharmacy', labelSinhala: 'ඖෂධ ශාලාව', href: '/pharmacy' },
    { label: 'Contact Us', labelSinhala: 'අප සම්බන්ධ කරන්න', href: '/contact' },
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
    { icon: Globe, href: 'https://gov.lk', label: 'Government Portal' },
  ];

  return (
    <footer className="bg-gradient-to-br from-text-dark to-primary-green text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="footer-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="2" fill="currentColor" opacity="0.3"/>
              <path d="M10,5 L15,10 L10,15 L5,10 Z" fill="currentColor" opacity="0.2"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#footer-pattern)" className="text-white"/>
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="py-12 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {/* Hospital Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="relative">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full overflow-hidden border-2 border-white/30 shadow-lg bg-white p-1">
                    <img 
                      src={logo} 
                      alt="Government Hospital Logo" 
                      className="w-full h-full object-contain rounded-full"
                    />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-gradient-to-br from-orange-400 via-white to-red-500 rounded-full border border-white/50 shadow-sm"></div>
                </div>
                <div>
                  <h3 className="text-lg md:text-xl font-bold">
                    {t('hospital-name-footer', 'Divisional Hospital', 'ප්‍රාදේශීය රෝහල')}
                  </h3>
                  <p className="text-sm md:text-base text-white/80 font-medium">
                    {t('location-footer', 'Weeraketiya', 'වීරකැටිය')}
                  </p>
                </div>
              </div>
              <p className="text-sm md:text-base text-white/90 leading-relaxed mb-4">
                {t(
                  'hospital-description',
                  'Providing quality healthcare services to the community of Weeraketiya and surrounding areas with compassion and excellence.',
                  'වීරකැටිය සහ අවට ප්‍රදේශවල ප්‍රජාවට කරුණාව සහ විශිෂ්ටත්වය සහිතව ගුණාත්මක සෞඛ්‍ය සේවා සැපයීම.'
                )}
              </p>
              <div className="flex gap-3 md:gap-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center hover:bg-white/20 transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.1 }}
                  >
                    <social.icon className="w-4 h-4 md:w-5 md:h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h4 className="text-base md:text-lg font-semibold mb-4 md:mb-6">
                {t('quick-links', 'Quick Links', 'ඉක්මන් සබැඳි')}
              </h4>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1 + index * 0.1 }}
                  >
                    <a
                      href={link.href}
                      className="text-sm md:text-base text-white/80 hover:text-accent-gold transition-colors duration-300 flex items-center gap-2 group"
                    >
                      <span className="w-1 h-1 bg-accent-gold rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {t(`link-${link.href.replace('/', '')}`, link.label, link.labelSinhala)}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h4 className="text-base md:text-lg font-semibold mb-4 md:mb-6">
                {t('contact-info', 'Contact Information', 'සම්බන්ධතා තොරතුරු')}
              </h4>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start gap-2 md:gap-3">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 text-accent-gold mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('general-inquiries', 'General Inquiries', 'සාමාන්‍ය විමසීම්')}
                    </p>
                    <a href="tel:+94472246260" className="text-sm md:text-base text-white/80 hover:text-accent-gold transition-colors duration-300">
                      +94 47 224 6260
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 text-error-red mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('emergency', 'Emergency', 'හදිසි')}
                    </p>
                    <a href="tel:+94472246260" className="text-sm md:text-base text-white/80 hover:text-error-red transition-colors duration-300">
                      +94 47 224 6260
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <Mail className="w-4 h-4 md:w-5 md:h-5 text-accent-gold mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">Email</p>
                    <a href="mailto:info@weeraketiyahospital.lk" className="text-xs sm:text-sm md:text-base text-white/80 hover:text-accent-gold transition-colors duration-300 break-all">
                      info@weeraketiyahospital.lk
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-2 md:gap-3">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-accent-gold mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('address', 'Address', 'ලිපිනය')}
                    </p>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Divisional+Hospital+Weeraketiya+Hambantota+Sri+Lanka"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm md:text-base text-white/80 hover:text-accent-gold transition-colors duration-300 inline-block"
                    >
                      Hospital Road, Weeraketiya<br />
                      Hambantota District<br />
                      Sri Lanka
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Operating Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <h4 className="text-base md:text-lg font-semibold mb-4 md:mb-6">
                {t('operating-hours', 'Operating Hours', 'ක්‍රියාකාරී වේලාවන්')}
              </h4>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-accent-gold flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('emergency-services', 'Emergency Services', 'හදිසි සේවාවන්')}
                    </p>
                    <p className="text-sm md:text-base text-white/80">24/7</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-accent-gold flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('outpatient', 'Outpatient Services', 'බාහිර රෝගී සේවාවන්')}
                    </p>
                    <p className="text-xs sm:text-sm md:text-base text-white/80">
                      {t('weekdays', 'Mon - Fri: 8:00 AM - 5:00 PM', 'සඳුදා - සිකුරාදා: 8:00 AM - 5:00 PM')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-accent-gold flex-shrink-0" />
                  <div>
                    <p className="text-sm md:text-base text-white/90 font-medium">
                      {t('visiting-hours', 'Visiting Hours', 'නැරඹුම් වේලාවන්')}
                    </p>
                    <p className="text-sm md:text-base text-white/80">
                      {t('visiting-time', '2:00 PM - 6:00 PM', '2:00 PM - 6:00 PM')}
                    </p>
                  </div>
                </div>
                <div className="bg-error-red/20 border border-error-red/30 rounded-lg p-3 md:p-4">
                  <p className="text-xs md:text-sm text-white/90 font-medium mb-1">
                    {t('covid-notice', 'COVID-19 Notice', 'COVID-19 දැනුම්දීම')}
                  </p>
                  <p className="text-xs text-white/80">
                    {t(
                      'covid-message', 
                      'Masks required. Limited visitors allowed.',
                      'වෙස් මුහුණු අනිවාර්යයි. සීමිත අමුත්තන්ට අවසර ඇත.'
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-white/20 py-6 md:py-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-white/80 text-xs sm:text-sm text-center md:text-left">
              © 2024 {t('hospital-copyright', 'Divisional Hospital Weeraketiya', 'ප්‍රාදේශීය රෝහල වීරකැටිය')}. 
              {t('rights-reserved', ' All rights reserved.', ' සියලු හිමිකම් ඇවිරිණි.')}
            </p>
            <div className="flex flex-wrap justify-center md:justify-end gap-4 md:gap-6 text-xs sm:text-sm">
              <a href="/privacy" className="text-white/80 hover:text-accent-gold transition-colors duration-300">
                {t('privacy-policy', 'Privacy Policy', 'පුද්ගලිකත්ව ප්‍රතිපත්තිය')}
              </a>
              <a href="/terms" className="text-white/80 hover:text-accent-gold transition-colors duration-300">
                {t('terms-service', 'Terms of Service', 'සේවා කොන්දේසි')}
              </a>
              <a href="/accessibility" className="text-white/80 hover:text-accent-gold transition-colors duration-300">
                {t('accessibility', 'Accessibility', 'ප්‍රවේශ්‍යත්වය')}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
